import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface BudgetItem {
  id: string;
  category: string;
  description: string;
  allocated_amount: number;
  spent_amount: number;
}

interface AdjustmentRecord {
  id: string;
  previous_amount: number;
  new_amount: number;
  adjustment_date: string;
  reason: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  budgetItem: BudgetItem | null;
  adjustmentHistory: AdjustmentRecord[];
  onSuccess: () => void;
}

type Tab = 'adjust' | 'history';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

export default function MobileBudgetAdjustmentSheet({ isOpen, onClose, budgetItem, adjustmentHistory, onSuccess }: Props) {
  const [tab, setTab] = useState<Tab>('adjust');
  const [newAmount, setNewAmount] = useState('');
  const [adjustmentDate, setAdjustmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && budgetItem) {
      setNewAmount(String(budgetItem.allocated_amount));
      setAdjustmentDate(new Date().toISOString().split('T')[0]);
      setReason('');
      setError('');
      setTab('adjust');
    }
  }, [isOpen, budgetItem]);

  const previousAmount = budgetItem?.allocated_amount ?? 0;
  const newAmountNum = parseFloat(newAmount) || 0;
  const diff = newAmountNum - previousAmount;
  const pct = previousAmount ? ((diff / previousAmount) * 100).toFixed(1) : '0.0';

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!reason.trim()) { setError('Reason for adjustment is required'); return; }
    if (isNaN(newAmountNum) || newAmountNum < 0) { setError('New amount must be 0 or greater'); return; }
    if (!budgetItem) return;

    try {
      setSaving(true);

      const { error: adjErr } = await supabase.from('budget_adjustments').insert([{
        budget_item_id: budgetItem.id,
        previous_amount: previousAmount,
        new_amount: newAmountNum,
        adjustment_date: adjustmentDate,
        reason: reason.trim(),
      }]);
      if (adjErr) throw adjErr;

      const { error: updateErr } = await supabase.from('budget_items').update({ allocated_amount: newAmountNum }).eq('id', budgetItem.id);
      if (updateErr) throw updateErr;

      toast.success('Budget adjusted successfully');
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to adjust budget');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !budgetItem) return null;

  const field = 'w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50';
  const label = 'block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide';

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-golf-gray">Budget Adjustment</h2>
            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[220px]">{budgetItem.description}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 flex-shrink-0">
          {(['adjust', 'history'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-3 text-sm font-medium capitalize transition-colors border-b-2 ${
                tab === t ? 'border-golf-orange text-golf-orange' : 'border-transparent text-gray-500'
              }`}
            >
              {t === 'adjust' ? 'Adjust Budget' : 'History'}
            </button>
          ))}
        </div>

        {tab === 'adjust' ? (
          <>
            <form onSubmit={handleSave} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{error}</div>
              )}

              <div className="bg-gray-50 rounded-xl px-4 py-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Current Amount</p>
                <p className="text-lg font-bold text-golf-gray">{formatCurrency(previousAmount)}</p>
              </div>

              <div>
                <label className={label}>New Amount (₦) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newAmount}
                  onChange={e => setNewAmount(e.target.value)}
                  className={field}
                />
              </div>

              {newAmount && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Difference</span>
                    <span className={`font-semibold ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-500' : 'text-gray-600'}`}>
                      {diff > 0 ? '+' : ''}{formatCurrency(diff)} ({diff > 0 ? '+' : ''}{pct}%)
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${diff >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(Math.abs((newAmountNum / (previousAmount || 1)) * 100), 100)}%` }}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className={label}>Adjustment Date *</label>
                <input
                  type="date"
                  value={adjustmentDate}
                  onChange={e => setAdjustmentDate(e.target.value)}
                  className={field}
                />
              </div>

              <div>
                <label className={label}>Reason for Adjustment *</label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={3}
                  placeholder="Explain why this budget adjustment is necessary"
                  className={`${field} resize-none`}
                />
              </div>

              <div className="pb-2" />
            </form>

            <div className="flex gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
              <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button type="submit" onClick={handleSave} disabled={saving} className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 disabled:opacity-60 transition-colors">
                {saving ? 'Saving...' : 'Save Adjustment'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {adjustmentHistory.length === 0 ? (
                <div className="py-16 text-center">
                  <p className="text-sm text-gray-400">No adjustment history found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {adjustmentHistory.map(adj => {
                    const d = adj.new_amount - adj.previous_amount;
                    return (
                      <div key={adj.id} className="bg-gray-50 rounded-2xl px-4 py-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-semibold text-golf-gray">{formatDate(adj.adjustment_date)}</p>
                          <span className={`text-sm font-bold ${d >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {d >= 0 ? '+' : ''}{formatCurrency(d)}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mb-2">
                          <span>Previous: {formatCurrency(adj.previous_amount)}</span>
                          <span>New: {formatCurrency(adj.new_amount)}</span>
                        </div>
                        <p className="text-xs text-gray-600"><span className="font-medium">Reason:</span> {adj.reason}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
              <button onClick={onClose} className="w-full py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors">
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
